import { Catalog } from '@df/catalog/catalog.interfaces';
import { ICmsModelCmsData } from '@df/catalog/cms/cms-model.interface';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { ICmsModel } from '../model/icms-model';
import { NICmsContentComponent } from '@icms/core/content';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { runInInjectionContext } from '@angular/core';
import { Injector } from '@angular/core';

export namespace NICmsBlock {
    /**
     * Block
     */
    export interface IData<D = any> extends Catalog.IBlockData {}
    /**
     * Verbosity 1 data
     */
    export interface IDataV4<D = any> extends IData<D> {
        _cms: ICmsData;
    }

    export interface ICmsData extends ICmsModelCmsData {
        scope?: 'website';
    }
}

/**
 * Page model representing WP pages
 */
export class ICmsBlock<T extends NICmsBlock.IData = NICmsBlock.IData> extends ICmsModel<T> {
    /**
     * Object type id
     * @type {String}
     */
    static override readonly type = 'block';

    /**
     * If model is routable
     * @type {Boolean}
     */
    static override readonly routable = true;

    /**
     * If uses drafts
     */
    override usesDrafts = true;

    /**
     * If model uses cms top bar for saving, undo and redo
     */
    override usesMainSaveBar = true;

    /**
     * Max verbosity level
     * @type {Number}
     */
    override maxVerbosity = 4; // 4 is cms

    /**
     * Services
     */
    @NgInject(ICmsContentService)
    private iCmsContentService!: ICmsContentService;

    @NgInject(Injector)
    private injector!: Injector;

    /**
     * Initidal data for sanitizer
     */
    protected override getInitialDataForSanitizer(data: T): Partial<T> | undefined {
        const meta = this.getMeta();
        if (meta) {
            const content = this.iCmsContentService.initFieldset<T['content'], ICmsBlock>(meta.fields, this, {}, meta.initialData);
            if (content) {
                return { content } as Partial<T>;
            }
        }
    }

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        const meta = this.getMeta();
        const resolvers = meta?.block?.resolvers;
        const loader = params.loader;

        if (resolvers) {
            loader.add(
                (async () => {
                    for (const resolver of Object.values(resolvers)) {
                        await runInInjectionContext(this.injector, () => resolver(this.data! as any));
                    }
                })()
            );
        }
    }

    getVariant(): string | undefined {
        return this.data!.url.split('@')[1];
    }

    getBareUrl(): string | undefined {
        return this.data!.url.split('@')[0];
    }

    private getMeta(): NICmsContentComponent.IMeta | undefined {
        return this.iCmsContentService.getComponentMeta(this.data!.url?.split('@')[0]);
    }
}
